import ProductAvailability from "helpers/product-availability";
import { usdPriceIntoFloat } from "helpers/PricingHelper";
import MembershipHelpers from "helpers/membership-helpers";
import FitRecHelpers from "helpers/fit-rec-helpers";
import { isClearance, isBulk } from "helpers/price-helper";
import { regionalToCanonical, regionalListToCanonicalList, getSelectedSku } from "helpers/sku-helpers";

const getLensAndAvailabilityType = (userData, product) => {
  let availabilityType, lens;

  // "Lenses" set here were defined by analytics and do NOT align with our site wide lenses.
  // They are for logging purposes only.
  if (isClearance(product?.price)) {
    lens = "purchase";
    availabilityType = "clearanceAvailabilities";
  } else if (isBulk(product?.price)) {
    lens = "purchase";
    availabilityType = "bulkAvailabilities";
  } else if (MembershipHelpers.isUnlimitedLens(userData)) {
    lens = "membership";
    availabilityType = "unlimitedAvailabilities";
  } else if (MembershipHelpers.isRtrUpdateLens(userData)) {
    lens = "membership";
    availabilityType = "rtrUpdateAvailabilities";
  } else if (MembershipHelpers.isClassicLens(userData)) {
    lens = "reserve";
    availabilityType = "rentalAvailabilities";
  } else {
    lens = "no_intent";
  }

  return { lens, availabilityType };
};

const getSizeAvailabilities = (lens, reservation, availabilityType, product) => {
  let availableSizes, unavailableSizes;

  // If no intent OR reserve with no dates selected, size fields can remain empty
  if (lens === "membership" || lens === "purchase" || (lens === "reserve" && reservation?.date)) {
    const allCanonicalSizesDeduped = regionalListToCanonicalList(
      product,
      product.skus.map(sku => sku.size)
    );

    const availableSizesNonCanon = availabilityType
      ? ProductAvailability.skusWithNonZeroCounts(product.skus, availabilityType).map(sku => sku.size)
      : [];

    // Note: For items that have multiple size lengths, canonically mapped sizes ignores length.
    // For example, if [SR - unavailable and SL - available], we will consider canonical size 4 available for heap logging
    // This behaivor is not a bug at this time and has been specifically requested by the analytics team
    availableSizes = regionalListToCanonicalList(product, availableSizesNonCanon);
    unavailableSizes = allCanonicalSizesDeduped
      ? allCanonicalSizesDeduped.filter(value => !availableSizes.includes(value))
      : null;

    return {
      availableSizes,
      unavailableSizes,
    };
  }
  return {};
};

const getMlRecommendedSize = (product, primarySize) => {
  if (product?.fitRates && primarySize) {
    const mlRecommendedSizes = FitRecHelpers.allRecommended(product?.fitRates, primarySize).map(fitRec => fitRec.size);
    return regionalListToCanonicalList(product, mlRecommendedSizes);
  }
  return null;
};

const getSelectedSize = (product, userData, membershipState, workingFilters, lens, reservation) => {
  if (lens === "membership" || lens === "purchase") {
    const size = ProductAvailability.getDefaultSizeSelection(product, userData, membershipState, workingFilters)?.size;
    const canonicalSize = regionalToCanonical(product, size);
    return canonicalSize;
  } else if (lens === "reserve") {
    return getSelectedSku(reservation, product.skus)?.canonicalSizes;
  }
  return null;
};

const getReserveDates = reservation => {
  return reservation?.date && reservation?.returnDate
    ? JSON.stringify([reservation.date, reservation.returnDate])
    : null;
};

const getPrices = (product, lens) => {
  // For members looking at clearance items, BE fetched data indicates that there is an adjusted discount price
  // but the data is misleading and membership discounts do not count towards clearance items per business rules
  if (lens === "purchase") {
    return {
      toBuyPrice: product?.price?.base,
      reservePrice: null,
      reservePriceMax: null,
      reservePriceMin: null,
    };
  } else {
    return {
      toBuyPrice: usdPriceIntoFloat(product?.purchasePrice) || null,
      reservePrice: product?.price?.adjusted || null,
      reservePriceMax: product?.price?.adjustedMaximum || null,
      reservePriceMin: product?.price?.adjustedMinimum || null,
    };
  }
};

const buildPDPEvents = (membershipState, userData, product, reservation, workingFilters, isComingSoon = false) => {
  const { lens = null, availabilityType = null } = getLensAndAvailabilityType(userData, product);
  const { availableSizes = null, unavailableSizes = null } = getSizeAvailabilities(
    lens,
    reservation,
    availabilityType,
    product
  );
  const mlRecommendedSize = getMlRecommendedSize(product, userData?.userProfile?.profiles?.primarySize);
  const selectedSize = getSelectedSize(product, userData, membershipState, workingFilters, lens, reservation);
  const { toBuyPrice, reservePrice, reservePriceMax, reservePriceMin } = getPrices(product, lens);

  let isMlSizeAvailable = null;
  if (Array.isArray(mlRecommendedSize) && Array.isArray(availableSizes)) {
    isMlSizeAvailable = mlRecommendedSize.some(element => availableSizes.includes(element));
  }

  let superBarSizeAvailable = null;
  if (Array.isArray(workingFilters?.canonicalSizes) && Array.isArray(availableSizes)) {
    superBarSizeAvailable = workingFilters?.canonicalSizes.some(element => availableSizes.includes(element));
  }

  return {
    lens: lens,
    available_sizes: JSON.stringify(availableSizes),
    unavailable_sizes: JSON.stringify(unavailableSizes),
    selected_size: JSON.stringify(selectedSize),
    ml_recommended_size: JSON.stringify(mlRecommendedSize),
    to_buy_price: toBuyPrice,
    reserve_price: reservePrice,
    reserve_price_max: reservePriceMax || null,
    reserve_price_min: reservePriceMin || null,
    reserve_dates: getReserveDates(reservation),
    rental_duration: reservation?.duration,
    is_users_superbar_size_available: JSON.stringify(superBarSizeAvailable),
    is_users_ml_size_available: JSON.stringify(isMlSizeAvailable),
    coming_soon: isComingSoon,
  };
};

const buildPDPDrawerEvents = (membershipState, userData, product, workingFilters, isComingSoon) => {
  const pdpEventObject = buildPDPEvents(membershipState, userData, product, null, workingFilters);
  return {
    available_sizes: pdpEventObject.available_sizes,
    selected_size: pdpEventObject.selected_size,
    is_users_superbar_size_available: pdpEventObject.is_users_superbar_size_available,
    is_users_ml_size_available: pdpEventObject.is_users_ml_size_available,
    lens: pdpEventObject.lens,
    ml_recommended_size: pdpEventObject.ml_recommended_size,
    reserve_price: pdpEventObject.reserve_price,
    reserve_price_max: pdpEventObject.reserve_price_max,
    reserve_price_min: pdpEventObject.reserve_price_min,
    styleName: product.id,
    to_buy_price: pdpEventObject.to_buy_price,
    unavailable_sizes: pdpEventObject.unavailable_sizes,
    coming_soon: isComingSoon,
  };
};

export { buildPDPEvents, buildPDPDrawerEvents };
